import { useState } from 'react'

import { Modal, ModalTitle } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalFooterWithActionsType } from '@/components/modal/constants'

interface RejectMatterInviteConfirmModalProps {
  matterName: string
  onClose: () => void
  /** Resolves with `null` on success, or an error message string to surface inline on failure. */
  onConfirm: () => Promise<string | null>
}

/**
 * Destructive confirmation shown before rejecting a matter invite. Stacks above the Accept
 * Matter Invite dialog and owns its own loading/error state; the success toast fires from the
 * parent dialog once the reject resolves.
 */
export function RejectMatterInviteConfirmModal({ matterName, onClose, onConfirm }: RejectMatterInviteConfirmModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setError(null)
    setLoading(true)

    try {
      const rejectError = await onConfirm()

      if (!rejectError) {
        // onClose unmounts this modal; the parent dialog surfaces the success toast.
        onClose()
      } else {
        setError(rejectError)
        setLoading(false)
      }
    } catch {
      // onConfirm resolves its own errors, but guard against an unexpected rejection
      // so the spinner never locks on and the user can retry or cancel.
      setError('Could not reject invite. Try again.')
      setLoading(false)
    }
  }

  return (
    <Modal open onOpenChange={(nextOpen) => !nextOpen && !loading && onClose()}>
      <ModalContainer width="sm" height="max-sm" stacked preventDismiss={loading} className="max-w-[448px]">
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-1.5">
            <ModalTitle>Reject invitation?</ModalTitle>
            <p className="font-sans text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">You'll lose access to {matterName}.</p>
          </div>
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
            primaryAction={{ label: 'Reject', onClick: handleConfirm, loading }}
          />
        </div>
      </ModalContainer>
    </Modal>
  )
}
